"use client";
import useUser from "@/lib/useUser";
import { useState } from "react";
import fetchJson from "@/lib/fetchJson";
import {
  Button,
  Callout,
  Flex,
  Text,
  TextField,
  IconButton,
} from "@radix-ui/themes";
import { IconExclamationCircle, IconTrash } from "@tabler/icons-react";
import * as Dialog from "@/components/CustomUiComponents/Dialog";
import { useCampaign } from "@/services/campaign";
import { useRouter } from "next/router";

export default function DeleteCampaign({
  campaignId,
}: {
  campaignId: number | undefined;
}) {
  const { user, mutateUser } = useUser();
  const { data: campaign, mutate } = useCampaign(
    campaignId != null ? String(campaignId) : undefined
  );
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");
  const router = useRouter();

  const deleteCampaign = async (e) => {
    setError("");
    if (!campaign || campaign.name !== confirmText) {
      setError(
        !campaign ? "Campaign could not be loaded" : "Campaign name does not match"
      );
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    try {
      const resp = await fetchJson(`/campaign/${campaignId}`, {
        method: "DELETE",
      });
      if (resp.error) {
        console.log(resp.error);
        e.preventDefault();
        e.stopPropagation();
        setError(resp.error);
      } else {
        setConfirmText("");
        router.push("/campaign");
      }
    } catch (e: any) {
      console.log(e);
    }
  };

  if (campaignId == null) {
    return null;
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <IconButton variant="ghost">
          <IconTrash className="icon" />
        </IconButton>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content>
          <Dialog.Title>Delete Campaign</Dialog.Title>

          <Flex direction="column" gap="3">
            <Text size="2">
              This action will delete the campaign and all associated data and{" "}
              <b>cannot be undone</b>.
            </Text>
            {error && (
              <Callout.Root color="red">
                <Callout.Icon>
                  <IconExclamationCircle />
                </Callout.Icon>
                <Callout.Text>{error}</Callout.Text>
              </Callout.Root>
            )}
            <label>
              <Text as="div" size="2" mb="1">
                Enter the campaign name to confirm
              </Text>
              <Text as="div" size="2" mb="1" weight="bold">
                {campaign?.name}
              </Text>
              <TextField.Root
                placeholder="Enter campaign name"
                onChange={(e) => {
                  setConfirmText(e.target.value);
                }}
                value={confirmText}
              />
            </label>
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                onClick={() => {
                  setConfirmText("");
                  setError("");
                }}
              >
                Cancel
              </Button>
            </Dialog.Close>
            <Dialog.Close>
              <Button onClick={deleteCampaign}>Delete</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
